/**
 * Background prefetch for Interview Schedule page.
 * After login, data is fetched in the background so the Interview page loads faster.
 * Cache is in-memory with a short TTL; cleared on logout.
 */

import { getApiUrl } from '../../../config/api';

const CACHE_TTL_MS = 2 * 60 * 1000; // 2 minutes

let cache = {
  jobs: null,
  candidatesByJobId: null,
  timestamp: 0,
};

function getCompanyEmail() {
  return localStorage.getItem('employer_email') || sessionStorage.getItem('employer_email');
}

function getCompanyId() {
  return (
    localStorage.getItem('companyAccountId') ||
    localStorage.getItem('companyId') ||
    localStorage.getItem('companyID') ||
    ''
  );
}

function isCacheValid() {
  return cache.timestamp && Date.now() - cache.timestamp < CACHE_TTL_MS;
}

/**
 * Returns cached jobs if valid. Used by InterviewSchedule on mount for instant display.
 */
export function getCachedJobs() {
  if (!cache.jobs || !isCacheValid()) return null;
  return cache.jobs;
}

/**
 * Returns cached candidates for a job if valid.
 */
export function getCachedCandidates(jobId) {
  if (!jobId || !cache.candidatesByJobId || !isCacheValid()) return null;
  return cache.candidatesByJobId[jobId] ?? null;
}

/**
 * Clear cache (e.g. on logout).
 */
export function clearInterviewPrefetchCache() {
  cache = { jobs: null, candidatesByJobId: null, timestamp: 0 };
}

/**
 * Start prefetching Interview data in the background (fire-and-forget).
 * Call this when user is authenticated (e.g. after login or when entering HireWithUs).
 */
export function startInterviewPrefetch() {
  const companyEmail = getCompanyEmail();
  const companyId = getCompanyId();
  if (!companyEmail || !companyId) return;

  const url = getApiUrl(
    `/api/get_jd?company_email=${encodeURIComponent(companyEmail)}&companyId=${companyId}`
  );

  fetch(url, {
    method: 'GET',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
  })
    .then((res) => (res.ok ? res.json() : null))
    .then((data) => {
      if (!data || !Array.isArray(data)) return;
      const jobs = data.map((job) => ({
        id: job.id || job.job_id,
        title: job.job_title,
        location: job.location || 'Remote-first',
        level: job.job_level || 'Junior, Mid-Level, Senior',
      }));
      cache.jobs = jobs;
      cache.timestamp = Date.now();

      // Optionally prefetch candidates for the first job so first view is fast
      const firstJobId = jobs[0]?.id;
      if (!firstJobId) return;

      const candidateUrl = getApiUrl(`/api/candidatelist?jobId=${firstJobId}`);
      fetch(candidateUrl, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
      })
        .then((r) => (r.ok ? r.json() : null))
        .then((candidateData) => {
          const arr = Array.isArray(candidateData) ? candidateData : candidateData?.candidates ?? [];
          if (!cache.candidatesByJobId) cache.candidatesByJobId = {};
          cache.candidatesByJobId[firstJobId] = arr;
        })
        .catch(() => {});
    })
    .catch(() => {});
}
